import {getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {StrandOfDoubleStranded} from 'constants/MolbioConstants';
import {
  CircularArrow,
  CircularArrowType,
} from 'molbio/components/visualizations/CircularFragmentVisualization';
import {
  DisplayInfo,
  PRIMER_STROKE_WIDTH,
} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import {NEAR_BLACK} from 'ui-library/Colors';
import Arc from 'ui-library/svg/Arc';
import {Angle, Point} from 'util/geometry/GeometryConstants';
import {polarToCartesian} from 'util/geometry/GeometryUtil';

const PRIMER_SIZE_AS_ANGLE = (30 * Math.PI) / 180;
const PRIMER_RADIUS_OFFSET = 8;

export interface CircularPrimersVisualizationProps {
  startDisplayInfo: DisplayInfo;
  endDisplayInfo: DisplayInfo;
  startAngle: Angle;
  endAngle: Angle;
  circleCenter: Point;
  radius: number;
  fragmentStrand: StrandOfDoubleStranded;
}

/**
 * Renders primers on a mini sequence visualization for circular sequences.
 * This component assumes that the fragment has a valid start and end
 */
export default function CircularPrimersVisualization({
  startDisplayInfo,
  endDisplayInfo,
  startAngle,
  endAngle,
  circleCenter,
  radius,
  fragmentStrand,
}: CircularPrimersVisualizationProps): JSX.Element {
  // Don't show primers if the fragment length is too small compared to the sequence,
  // unless the start and end angle are the same, meaning the fragment is the whole sequence.
  if (
    (!startDisplayInfo.primer && !endDisplayInfo.primer) ||
    (Math.abs(startAngle - endAngle) < PRIMER_SIZE_AS_ANGLE && startAngle !== endAngle)
  ) {
    return <></>;
  }
  // Always show the 5' primer inside the sequence circle and the 3' primer outside
  const fivePrimeRadius = radius - PRIMER_RADIUS_OFFSET;
  const threePrimeRadius = radius + PRIMER_RADIUS_OFFSET;
  const primerArcLength = PRIMER_SIZE_AS_ANGLE * radius;
  let startRadius;
  let endRadius;
  let startArrowAngle;
  let endArrowAngle;
  let startArrowType;
  let endArrowType;
  if (fragmentStrand === 1) {
    startRadius = fivePrimeRadius;
    endRadius = threePrimeRadius;
    startArrowAngle = startAngle + primerArcLength / fivePrimeRadius;
    endArrowAngle = endAngle - primerArcLength / threePrimeRadius;
    startArrowType = CircularArrowType.INSIDE;
    endArrowType = CircularArrowType.OUTSIDE;
  } else {
    startRadius = threePrimeRadius;
    endRadius = fivePrimeRadius;
    startArrowAngle = startAngle + primerArcLength / threePrimeRadius;
    endArrowAngle = endAngle - primerArcLength / fivePrimeRadius;
    startArrowType = CircularArrowType.OUTSIDE;
    endArrowType = CircularArrowType.INSIDE;
  }
  const startPrimerArrowCenter = polarToCartesian(circleCenter, startRadius, startArrowAngle);
  const endPrimerArrowCenter = polarToCartesian(circleCenter, endRadius, endArrowAngle);

  return (
    <g {...getTestProps({component: 'CircularPrimersVisualization', element: 'root'})}>
      {startDisplayInfo.primer && (
        <>
          <Arc
            dataTestAttrs={{component: 'CircularPrimersVisualization', element: 'startPrimerArc'}}
            circleCenter={circleCenter}
            radius={startRadius}
            startAngle={startAngle}
            endAngle={startArrowAngle}
            pathProps={{stroke: NEAR_BLACK, strokeWidth: PRIMER_STROKE_WIDTH}}
          />
          <CircularArrow
            circleCenter={circleCenter}
            radius={startRadius}
            arrowCenter={startPrimerArrowCenter}
            circleAngle={startArrowAngle}
            direction={1}
            stroke={NEAR_BLACK}
            strokeWidth={PRIMER_STROKE_WIDTH}
            arrowType={startArrowType}
          />
        </>
      )}

      {endDisplayInfo.primer && (
        <>
          <Arc
            dataTestAttrs={{component: 'CircularPrimersVisualization', element: 'endPrimerArc'}}
            circleCenter={circleCenter}
            radius={endRadius}
            startAngle={endArrowAngle}
            endAngle={endAngle}
            pathProps={{stroke: NEAR_BLACK, strokeWidth: PRIMER_STROKE_WIDTH}}
          />
          <CircularArrow
            circleCenter={circleCenter}
            radius={endRadius}
            arrowCenter={endPrimerArrowCenter}
            circleAngle={endArrowAngle}
            direction={-1}
            stroke={NEAR_BLACK}
            strokeWidth={PRIMER_STROKE_WIDTH}
            arrowType={endArrowType}
          />
        </>
      )}
    </g>
  );
}
